import React, { useState } from 'react';
import {
    Modal,
    Button,
    Row,
    Col,
    Badge,
    Form,
} from 'react-bootstrap';
import './TaskSchedulerModal.scss';

const TaskSchedulerModal = ({ show, onHide, onSave }) => {
    const [recurrenceType, setRecurrenceType] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
    const [hour, setHour] = useState(10);
    const [minute, setMinute] = useState(0);
    const [ampm, setAmpm] = useState('AM');
    const [weeklyDays, setWeeklyDays] = useState({
        Mon: false, Tue: false, Wed: false,
        Thu: false, Fri: false, Sat: false, Sun: false,
    });
    const [monthlyDays, setMonthlyDays] = useState([]);

    const toggleWeekDay = day =>
        setWeeklyDays(ws => ({ ...ws, [day]: !ws[day] }));

    const toggleMonthDay = day =>
        setMonthlyDays(md =>
            md.includes(day) ? md.filter(d => d !== day) : [...md, day]
        );

    const formatTwo = num => String(num).padStart(2, '0');

    // Build a cron expression:
    const buildCron = () => {
        // Convert 12h → 24h
        let h24 = hour % 12 + (ampm === 'PM' ? 12 : 0);
        let mm = minute;
        const m = mm;
        const H = h24;

        if (recurrenceType === 'daily') {
            return `${m} ${H} * * *`;
        }

        if (recurrenceType === 'weekly') {
            const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
            const days = Object.entries(weeklyDays)
                .filter(([, on]) => on)
                .map(([d]) => map[d])
                .join(',');
            return `${m} ${H} * * ${days || '*'}`;
        }

        if (recurrenceType === 'monthly') {
            const days = monthlyDays.join(',');
            return `${m} ${H} ${days || '*'} * *`;
        }

        return `${m} ${H} * * *`;
    };

    const handleSave = () => {
        const cronExpression = buildCron();
        const jobData = { cronExpression, status: 'Active' };
        onSave(jobData);
    };

    // --- UI renderers ---

    const renderTimePicker = () => (
        <div className="d-flex align-items-center">
            <div className='me-2'>
                <Form.Control
                    type="number"
                    min={1}
                    max={12}
                    value={hour}
                    onChange={e => setHour(Number(e.target.value))}
                />
            </div>
            <div>
                :
            </div>
            <div className='ms-2 me-2'>
                <Form.Control
                    type="number"
                    min={0}
                    max={59}
                    value={minute}
                    onChange={e => setMinute(Number(e.target.value))}
                />
            </div>
            <div>
                <Form.Check
                    
                    type="radio"
                    label="AM"
                    id="am"
                    checked={ampm === 'AM'}
                    onChange={() => setAmpm('AM')}
                />
                <Form.Check
                    
                    type="radio"
                    label="PM"
                    id="pm"
                    checked={ampm === 'PM'}
                    onChange={() => setAmpm('PM')}
                />
            </div>
        </div>
    );

    const renderWeekly = () => (
        <>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className={`d-flex justify-content-between week-row ${weeklyDays[day] ? 'selected' : ''}`}>
                    <div className='day-name'>{day}</div>
                    <div>
                        <Form.Check
                            type="switch"
                            id={`switch-${day}`}
                            label={weeklyDays[day] ? 'Active' : 'Inactive'}
                            checked={weeklyDays[day]}
                            onChange={() => toggleWeekDay(day)}
                        />
                    </div>
                </div>
            ))}
            <h6 className='text-center'>At this hour</h6>
            <div className="time-picker">
                {renderTimePicker()}
            </div>
        </>
    );

    const renderMonthly = () => {
        const days = Array.from({ length: 31 }, (_, i) => i + 1);
        return (
            <>
                <div
                    className='calendar-grid mt-2'
                >
                    {days.map(d => {
                        const selected = monthlyDays.includes(d);
                        return (
                            <Badge
                                key={d}
                                pill
                                bg={selected ? 'primary' : 'none'}
                                text={selected ? undefined : 'dark'}
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleMonthDay(d)}
                            >
                                {formatTwo(d)}
                            </Badge>
                        );
                    })}
                </div>
                <h6>At this hour</h6>
                <div className="time-picker">
                    {renderTimePicker()}
                </div>
            </>
        );
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" className='task-scheduler-modal'>
            <Modal.Header closeButton>
                <Modal.Title>Task Scheduler</Modal.Title>
            </Modal.Header>
            <Modal.Body className=''>
                <h6>Choose the recurrence of your tasks</h6>
                <div className="recurrence-options">
                    {['daily', 'weekly', 'monthly'].map(type => (
                        <Badge
                            key={type}
                            className={recurrenceType === type ? 'active' : ''}
                            onClick={() => setRecurrenceType(type)}
                            bg="none"
                        >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                        </Badge>
                    ))}
                </div>

                {recurrenceType === 'daily' && (
                    <>
                    <h6>Pick a time to schedule</h6>
                    <div className="time-picker">
                        {renderTimePicker()}
                    </div>
                    </>
                )}

                {recurrenceType === 'weekly' && (
                    <>
                    <h6>Run on these days</h6>
                    <div className="weekly-schedule">
                        {renderWeekly()}
                    </div>
                    </>
                )}

                {recurrenceType === 'monthly' && (
                    <>
                    <h6>Run on these days</h6>
                    <div className="monthly-schedule">
                        <small className="text-muted">
                            Select the days of the month you want to automatically run the task
                        </small>
                        {renderMonthly()}
                    </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>Save</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TaskSchedulerModal;
